import { ResizeNotifier } from '@theme/utilities';
import { DeclarativeShadowElement } from '@theme/component';

/**
 * Event class for overflow minimum items updates.
 * @extends {Event}
 */
export class OverflowMinimumEvent extends Event {
  /**
   * @param {boolean} minimumReached
   */
  constructor(minimumReached) {
    super('overflowMinimum', { bubbles: true });

    this.detail = {
      minimumReached,
    };
  }
}

/**
 * A custom element that moves list items to an overflow slot when they do not fit.
 *
 * @attr {string | null} minimum-items
 */
export class OverflowList extends DeclarativeShadowElement {
  static get observedAttributes() {
    return ['disabled', 'minimum-items'];
  }

  /**
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue || !this.#refs) return;

    if (name === 'disabled') {
      if (newValue === 'true') {
        this.#reset();
      } else {
        this.#scheduleReflow();
      }

      return;
    }

    if (name === 'minimum-items') {
      this.#scheduleReflow();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  /**
   * Initializes the element.
   */
  #initialize() {
    const { shadowRoot } = this;

    if (!shadowRoot) {
      throw new Error('Missing shadow root');
    }

    const defaultSlot = shadowRoot.querySelector('slot:not([name])');
    const overflowSlot = shadowRoot.querySelector('slot[name="overflow"]');
    const moreSlot = shadowRoot.querySelector('slot[name="more"]');
    const overflow = shadowRoot.querySelector('[part="overflow"]');
    const list = shadowRoot.querySelector('[part="list"]');
    const placeholder = shadowRoot.querySelector('[part="placeholder"]');

    if (
      !(defaultSlot instanceof HTMLSlotElement) ||
      !(overflowSlot instanceof HTMLSlotElement) ||
      !(moreSlot instanceof HTMLSlotElement) ||
      !(overflow instanceof HTMLElement) ||
      !(list instanceof HTMLUListElement) ||
      !(placeholder instanceof HTMLLIElement)
    ) {
      throw new Error('Invalid element types in <OverflowList />');
    }

    this.#refs = {
      defaultSlot,
      overflowSlot,
      moreSlot,
      overflow,
      list,
      placeholder,
    };

    this.addEventListener('reflow', this.#handleReflowRequest);

    const elements = defaultSlot
      .assignedElements()
      .filter((element) => !(element instanceof HTMLTemplateElement));

    const firstElement = elements[0];
    const lastElement = elements[elements.length - 1];

    /*
     * Wait until the component is close to the viewport before measuring it.
     * IntersectionObserver supplies the initial height without an extra layout read.
     */
    if (firstElement) {
      this.#intersectionObserver.observe(firstElement);
    }

    if (lastElement && lastElement !== firstElement) {
      this.#intersectionObserver.observe(lastElement);
    }

    /*
     * Empty lists do not produce an intersection entry, so initialize them directly.
     */
    if (!firstElement) {
      this.#scheduleReflow();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    this.removeEventListener('reflow', this.#handleReflowRequest);

    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#intersectionObserver.disconnect();

    this.#observersActive = false;

    if (this.#frameId !== null) {
      cancelAnimationFrame(this.#frameId);
      this.#frameId = null;
    }
  }

  /**
   * @returns {number | null}
   */
  get minimumItems() {
    const value = this.getAttribute('minimum-items');
    return value ? Number.parseInt(value, 10) : null;
  }

  get overflowSlot() {
    return this.#refs?.overflowSlot;
  }

  get defaultSlot() {
    return this.#refs?.defaultSlot;
  }

  /**
   * @param {CustomEvent<{lastVisibleElement?: HTMLElement}>} event
   */
  #handleReflowRequest = (event) => {
    this.#scheduleReflow(0, event.detail?.lastVisibleElement ?? null);
  };

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #handleIntersection = (entries) => {
    const entry = entries.find((item) => item.isIntersecting);

    if (!entry) return;

    this.#intersectionObserver.disconnect();

    /*
     * Dynamically injected declarative shadow roots can leave their template
     * in the light DOM. Remove it before starting the MutationObserver.
     */
    this.querySelector(':scope > template[shadowrootmode="open"]')?.remove();

    this.#scheduleReflow(entry.boundingClientRect.height);
  };

  /**
   * ResizeNotifier callback.
   *
   * React only to width changes. Internal height changes do not require
   * recalculating which items fit and previously caused unnecessary reflows.
   *
   * @param {ResizeObserverEntry[]} entries
   */
  #handleResize = (entries) => {
    const width = entries?.[0]?.contentRect?.width;

    if (
      Number.isFinite(width) &&
      Math.abs(width - this.#lastMeasuredWidth) < 0.5
    ) {
      return;
    }

    this.#scheduleReflow();
  };

  /**
   * MutationObserver callback.
   */
  #handleMutation = () => {
    this.#scheduleReflow();
  };

  /**
   * Coalesces every reflow request into a single animation frame.
   *
   * @param {number} [listHeight]
   * @param {HTMLElement | null} [lastVisibleElement]
   */
  #scheduleReflow(listHeight = 0, lastVisibleElement = null) {
    if (!this.isConnected || !this.#refs) return;

    if (listHeight > 0) {
      this.#pendingListHeight = listHeight;
    }

    if (lastVisibleElement) {
      this.#pendingLastVisibleElement = lastVisibleElement;
    }

    if (this.#frameId !== null) return;

    this.#frameId = requestAnimationFrame(() => {
      this.#frameId = null;

      const pendingHeight = this.#pendingListHeight;
      const pendingLastVisible = this.#pendingLastVisibleElement;

      this.#pendingListHeight = 0;
      this.#pendingLastVisibleElement = null;

      this.#reflowItems(pendingHeight, pendingLastVisible);
    });
  }

  /**
   * Moves all overflowed items back into the default slot.
   */
  #moveItemsToDefaultSlot() {
    const { defaultSlot, overflowSlot } = this.#refs;

    for (const element of overflowSlot.assignedElements()) {
      if (element.slot !== defaultSlot.name) {
        element.slot = defaultSlot.name;
      }
    }
  }

  /**
   * Resets the component.
   */
  #reset() {
    const { list, placeholder, moreSlot } = this.#refs;

    this.#unobserveChanges();
    this.#moveItemsToDefaultSlot();

    list.style.removeProperty('height');
    list.style.removeProperty('flex-wrap');
    list.style.removeProperty('counter-reset');
    list.style.removeProperty('overflow');

    moreSlot.style.removeProperty('order');
    moreSlot.hidden = true;

    placeholder.style.removeProperty('width');
    placeholder.hidden = true;

    this.style.setProperty('--overflow-count', '0');
  }

  /**
   * @param {Element[]} visibleElements
   */
  #updateMinimumReached(visibleElements) {
    const minimumItems = this.minimumItems;

    if (minimumItems === null) return;

    const minimumReached = visibleElements.length < minimumItems;
    const alreadyReached = this.hasAttribute('minimum-reached');

    if (minimumReached === alreadyReached) return;

    this.toggleAttribute('minimum-reached', minimumReached);
    this.dispatchEvent(new OverflowMinimumEvent(minimumReached));
  }

  /**
   * Shows all items.
   */
  showAll() {
    const { placeholder } = this.#refs;

    placeholder.style.setProperty('width', '0');
    placeholder.style.setProperty('display', 'none');

    this.setAttribute('disabled', 'true');
  }

  /**
   * Measures the list once and moves overflowing elements afterward.
   * DOM writes are completed before the measurement pass, and no writes occur
   * between getBoundingClientRect calls. This avoids repeated forced layouts.
   *
   * @param {number} [listHeight]
   * @param {HTMLElement | null} [lastVisibleElement]
   */
  #reflowItems = (listHeight = 0, lastVisibleElement = null) => {
    if (!this.#refs || this.getAttribute('disabled') === 'true') return;

    const {
      defaultSlot,
      overflowSlot,
      moreSlot,
      list,
      placeholder,
    } = this.#refs;

    /*
     * Temporarily pause the MutationObserver only. Slot changes do not change
     * childList, but this protects dynamically injected content during the pass.
     * The ResizeObserver remains connected and ignores unchanged widths.
     */
    this.#mutationObserver.disconnect();

    this.#moveItemsToDefaultSlot();

    const elements = defaultSlot
      .assignedElements()
      .filter((element) => !(element instanceof HTMLTemplateElement));

    if (!elements.length) {
      moreSlot.hidden = true;
      placeholder.hidden = true;
      this.style.setProperty('--overflow-count', '0');

      this.#observeChanges();
      return;
    }

    if (listHeight > 0) {
      list.style.setProperty('height', `${listHeight}px`);
    }

    /*
     * Write phase: prepare one wrapped row for measurement.
     */
    list.style.setProperty('flex-wrap', 'wrap');
    placeholder.hidden = true;

    moreSlot.style.setProperty('order', '-1');
    moreSlot.hidden = false;

    lastVisibleElement?.style.setProperty('order', '-1');

    /*
     * Read phase: the first rectangle flushes layout once. All following reads
     * reuse the same calculated layout because no DOM writes happen in between.
     */
    const hostRect = this.getBoundingClientRect();
    const moreSlotRect = moreSlot.getBoundingClientRect();

    this.#lastMeasuredWidth = hostRect.width;

    /** @type {Element[]} */
    const visibleElements = [];

    /** @type {Element[]} */
    const overflowingElements = [];

    let placeholderWidth = 0;

    for (const element of elements) {
      const elementRect = element.getBoundingClientRect();

      if (elementRect.top > moreSlotRect.top + 0.5) {
        if (overflowingElements.length === 0) {
          placeholderWidth = elementRect.width;
        }

        overflowingElements.push(element);
      } else {
        visibleElements.push(element);
      }
    }

    const hasOverflow = overflowingElements.length > 0;
    const overflowingSet = new Set(overflowingElements);

    /*
     * Final write phase.
     */
    if (hasOverflow) {
      moreSlot.style.removeProperty('order');
    }

    lastVisibleElement?.style.removeProperty('order');

    for (const element of elements) {
      const targetSlot = overflowingSet.has(element)
        ? overflowSlot.name
        : defaultSlot.name;

      if (element.slot !== targetSlot) {
        element.slot = targetSlot;
      }
    }

    list.style.setProperty(
      'counter-reset',
      `overflow-count ${overflowingElements.length}`
    );

    this.style.setProperty(
      '--overflow-count',
      String(overflowingElements.length)
    );

    moreSlot.hidden = !hasOverflow;

    if (hasOverflow) {
      placeholder.style.width = `${placeholderWidth}px`;
      placeholder.hidden = false;
      this.#updateMinimumReached(visibleElements);
    } else {
      placeholder.style.removeProperty('width');
      placeholder.hidden = true;
      this.removeAttribute('minimum-reached');
    }

    /*
     * Children may contain dropdowns or popovers that must escape the list.
     */
    list.style.setProperty('overflow', 'unset');

    this.#observeChanges();
  };

  #observeChanges() {
    if (!this.#observersActive) {
      this.#resizeObserver.observe(this);
      this.#observersActive = true;
    }

    this.#mutationObserver.observe(this, {
      childList: true,
    });
  }

  #unobserveChanges() {
    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#observersActive = false;
  }

  /**
   * @type {{
   *   defaultSlot: HTMLSlotElement;
   *   overflowSlot: HTMLSlotElement;
   *   moreSlot: HTMLSlotElement;
   *   overflow: HTMLElement;
   *   list: HTMLUListElement;
   *   placeholder: HTMLLIElement;
   * } | null}
   */
  #refs = null;

  /** @type {number | null} */
  #frameId = null;

  /** @type {number} */
  #pendingListHeight = 0;

  /** @type {HTMLElement | null} */
  #pendingLastVisibleElement = null;

  /** @type {number} */
  #lastMeasuredWidth = -1;

  /** @type {boolean} */
  #observersActive = false;

  /**
   * @type {ResizeObserver}
   */
  #resizeObserver = new ResizeNotifier(this.#handleResize);

  /**
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver(this.#handleMutation);

  #intersectionObserver = new IntersectionObserver(this.#handleIntersection, {
    rootMargin: '320px 180px 320px 180px',
  });
}

if (!customElements.get('overflow-list')) {
  customElements.define('overflow-list', OverflowList);
}